/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { nodeActionLabel } from '@sportninja/common/features/competition/operations/utils/nodeActionLabels'
import { resolveNewChildType } from '@sportninja/common/features/competition/operations/utils/resolveChildType'
import { isScheduleNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import type { MouseEvent } from 'react'
import Icon from 'src/components/Icon'
import { canAddChild, canDeleteNode } from '../../utils/canAddChild'

const translate = t as (key: string, options?: unknown) => string

interface TreeNodeActionsProps {
  node: OperationsNode
  canEdit: boolean
  /** Suppresses Add Team while registration is open — see `canAddChild`. */
  isRegistrationOn?: boolean
  onAdd: (node: OperationsNode, kind: 'division' | 'team') => void
  onEdit: (node: OperationsNode) => void
  onDelete: (node: OperationsNode) => void
}

/**
 * The per-row add / edit / delete buttons, kept from the old Setup tab. They
 * stay hidden until the row is hovered, focused or selected (see the parent's
 * `.tree-row-actions` rule) so the rail reads clean at rest.
 */
const TreeNodeActions = ({
  node,
  canEdit,
  isRegistrationOn = false,
  onAdd,
  onEdit,
  onDelete,
}: TreeNodeActionsProps) => {
  'use memo'

  if (!canEdit) {
    return null
  }

  const stop = (event: MouseEvent) => event.stopPropagation()
  const addable = canAddChild(node, isRegistrationOn)
  const deletable = canDeleteNode(node, isRegistrationOn)

  // Node-aware wording — "Edit Team", "Delete Division" — from the shared
  // helper the grids also use, so the two surfaces cannot drift apart.
  const editLabel = capitalizeWords(
    nodeActionLabel('edit', node.type, translate)
  )
  const deleteLabel = capitalizeWords(
    nodeActionLabel('delete', node.type, translate)
  )
  const addTeamLabel = capitalizeWords(translate('Web:addTeam'))

  // The child of a season may be a CONFERENCE, not a division — the same
  // question `DivisionModal` asks when this button opens it, so the tooltip
  // and the modal's Type field always agree.
  const childType = isScheduleNode(node)
    ? resolveNewChildType(node).type
    : 'Division'
  const addDivisionLabel = capitalizeWords(
    translate('common:addEntity', {
      entity: translate(`common:${childType.toLowerCase()}`),
    })
  )

  return (
    <span css={actionsStyle} className='tree-row-actions'>
      {addable.division && (
        <Tooltip title={addDivisionLabel} placement='top'>
          <button
            type='button'
            aria-label={addDivisionLabel}
            css={actionButton}
            onClick={(event) => {
              stop(event)
              onAdd(node, 'division')
            }}
          >
            <Icon name='folder-plus' fontSize={11} color={colors.NEUTRAL_50} />
          </button>
        </Tooltip>
      )}
      {addable.team && (
        <Tooltip title={addTeamLabel} placement='top'>
          <button
            type='button'
            aria-label={addTeamLabel}
            css={actionButton}
            onClick={(event) => {
              stop(event)
              onAdd(node, 'team')
            }}
          >
            <Icon name='user-plus' fontSize={11} color={colors.NEUTRAL_50} />
          </button>
        </Tooltip>
      )}
      <Tooltip title={editLabel} placement='top'>
        <button
          type='button'
          aria-label={editLabel}
          css={actionButton}
          onClick={(event) => {
            stop(event)
            onEdit(node)
          }}
        >
          <Icon name='pen' fontSize={11} color={colors.NEUTRAL_50} />
        </button>
      </Tooltip>
      {deletable && (
        <Tooltip title={deleteLabel} placement='top'>
          <button
            type='button'
            aria-label={deleteLabel}
            css={[actionButton, dangerButton]}
            onClick={(event) => {
              stop(event)
              onDelete(node)
            }}
          >
            <Icon name='trash' fontSize={11} color={colors.NEUTRAL_50} />
          </button>
        </Tooltip>
      )}
    </span>
  )
}

/* Stretches to the row's full height and centres within it, like the drag
   grip on the other edge — these act on the whole row, not on the name line
   the badge and twisty sit on. */
const actionsStyle = css`
  display: flex;
  align-self: stretch;
  align-items: center;
  gap: 3px;
  flex: none;
`

const actionButton = css`
  width: 24px;
  height: 24px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_200};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const dangerButton = css`
  &:hover {
    background: ${colors.ERROR_300};
  }
`

export default TreeNodeActions
